export const limits = {
  id: 'limits',
  title: 'Limits',
  standards: {
    commonCore: ['HSF-IF.C.7', 'HSF-LE.A.4'],
    nctm: ['Algebra: Analyze change in various contexts', 'Calculus: Understand the concept of limit'],
    states: {
      ny: ['PC-F.IF.7', 'PC-F.LE.4'],
      oh: ['PC.LIM.1', 'PC.LIM.2'],
      pa: ['CC.2.2.HS.C.5', 'CC.2.2.HS.D.10'],
      va: ['PC.11', 'PC.12'],
      tx: ['PC.2A', 'PC.2B']
    }
  },
  explanation: `# Understanding Limits - The Foundation of Calculus

Hello! Mr. Augustine here. Limits might sound scary, but I promise to make them crystal clear!

## What is a Limit?

A **limit** describes what value a function approaches as the input gets closer and closer to a certain number.

**Think of it like this:** Imagine you're walking toward a door. The limit is where you're heading, even if you never quite get there!

**Notation:** lim[x→a] f(x) = L

This reads: "The limit of f(x) as x approaches a equals L"

## Intuitive Understanding

**Example:** Let's say f(x) = (x² - 1)/(x - 1)

What happens as x gets close to 1?

Let's make a table:

| x | f(x) |
|---|------|
| 0.9 | 1.9 |
| 0.99 | 1.99 |
| 0.999 | 1.999 |
| 1.001 | 2.001 |
| 1.01 | 2.01 |
| 1.1 | 2.1 |

**Notice:** As x gets closer to 1, f(x) gets closer to 2!

So we write: lim[x→1] (x² - 1)/(x - 1) = 2

**Important:** The function doesn't have to be defined at x = 1. We only care about what it approaches!

## Three Ways a Limit Can Behave

**Case 1: The limit exists**
The function approaches the same value from both sides.

**Case 2: The limit does not exist**
- The function approaches different values from left and right
- The function oscillates wildly
- The function goes to infinity

**Case 3: The function has a hole or jump**
The limit might exist even if the function isn't defined there!

## One-Sided Limits

Sometimes we need to look at what happens from just one direction.

**Left-hand limit:** lim[x→a⁻] f(x) (approaching from the left)

**Right-hand limit:** lim[x→a⁺] f(x) (approaching from the right)

**Mr. Augustine's Rule:**
For a limit to exist at x = a:
lim[x→a⁻] f(x) = lim[x→a⁺] f(x)

Both sides must agree!

**Example:** Consider f(x) = |x|/x

As x → 0⁻ (from left): f(x) → -1
As x → 0⁺ (from right): f(x) → 1

Since -1 ≠ 1, lim[x→0] |x|/x does NOT exist!

## Finding Limits - Mr. Augustine's Methods

### Method 1: Direct Substitution (Easiest!)

**Step 1:** Try plugging in the value directly

**Example:** lim[x→3] (2x² + 5x - 1)

Just substitute x = 3:
= 2(3)² + 5(3) - 1
= 18 + 15 - 1
= 32

**Answer:** 32

**When it works:** When you don't get 0/0 or ∞/∞

### Method 2: Factoring (For 0/0 forms)

When direct substitution gives 0/0, try factoring!

**Example:** lim[x→2] (x² - 4)/(x - 2)

**Step 1:** Direct substitution gives 0/0 (indeterminate)

**Step 2:** Factor the numerator
(x² - 4)/(x - 2) = (x - 2)(x + 2)/(x - 2)

**Step 3:** Cancel common factors
= x + 2 (for x ≠ 2)

**Step 4:** Now substitute x = 2
= 2 + 2 = 4

**Answer:** 4

### Method 3: Rationalizing (For radicals)

When you have square roots, multiply by the conjugate!

**Example:** lim[x→0] (√(x + 4) - 2)/x

**Step 1:** Direct substitution gives 0/0

**Step 2:** Multiply by conjugate
= [(√(x + 4) - 2)/x] × [(√(x + 4) + 2)/(√(x + 4) + 2)]

**Step 3:** Simplify numerator (difference of squares)
= [(x + 4) - 4]/[x(√(x + 4) + 2)]
= x/[x(√(x + 4) + 2)]

**Step 4:** Cancel x
= 1/(√(x + 4) + 2)

**Step 5:** Substitute x = 0
= 1/(√4 + 2) = 1/(2 + 2) = 1/4

**Answer:** 1/4

## Limits at Infinity

What happens as x gets really, really large?

**Example:** lim[x→∞] (3x² + 2x - 1)/(x² - 5)

**Mr. Augustine's Trick:** Divide everything by the highest power of x in the denominator!

**Step 1:** Highest power in denominator is x²

**Step 2:** Divide every term by x²
= (3 + 2/x - 1/x²)/(1 - 5/x²)

**Step 3:** As x → ∞, terms with x in denominator → 0
= (3 + 0 - 0)/(1 - 0)
= 3/1
= 3

**Answer:** 3

## Special Limits to Remember

**1. Constant function:**
lim[x→a] c = c

**2. Identity function:**
lim[x→a] x = a

**3. Power function:**
lim[x→a] xⁿ = aⁿ

**4. Important trigonometric limit:**
lim[x→0] (sin x)/x = 1

## Limit Laws - Your Calculation Tools

If lim[x→a] f(x) = L and lim[x→a] g(x) = M, then:

**1. Sum Rule:**
lim[x→a] [f(x) + g(x)] = L + M

**2. Difference Rule:**
lim[x→a] [f(x) - g(x)] = L - M

**3. Product Rule:**
lim[x→a] [f(x) × g(x)] = L × M

**4. Quotient Rule:**
lim[x→a] [f(x)/g(x)] = L/M (if M ≠ 0)

**5. Constant Multiple Rule:**
lim[x→a] [c × f(x)] = c × L

**6. Power Rule:**
lim[x→a] [f(x)]ⁿ = Lⁿ

## Continuity - When Limits Match Function Values

A function is **continuous** at x = a if:
1. f(a) is defined
2. lim[x→a] f(x) exists
3. lim[x→a] f(x) = f(a)

**All three must be true!**

**Example:** f(x) = x² is continuous everywhere because:
- It's defined for all x
- The limit exists at every point
- The limit equals the function value

## Types of Discontinuities

**1. Removable (Hole):**
The limit exists but doesn't equal f(a), or f(a) is undefined.
Example: f(x) = (x² - 1)/(x - 1) at x = 1

**2. Jump:**
Left and right limits exist but are different.
Example: f(x) = |x|/x at x = 0

**3. Infinite:**
The function goes to ±∞.
Example: f(x) = 1/x at x = 0

## The Squeeze Theorem

If f(x) ≤ g(x) ≤ h(x) near x = a, and
lim[x→a] f(x) = lim[x→a] h(x) = L,
then lim[x→a] g(x) = L

**Think of it like this:** If g(x) is squeezed between two functions that both approach L, then g(x) must also approach L!

## Practical Problem-Solving Strategy

**Step 1:** Try direct substitution first

**Step 2:** If you get 0/0 or ∞/∞:
- Try factoring and canceling
- Try rationalizing (if radicals present)
- Try dividing by highest power (if limit at infinity)

**Step 3:** Check one-sided limits if needed

**Step 4:** Use limit laws to break complex limits into simpler parts

## Key Takeaways

✓ Limits describe approaching behavior, not necessarily the value at that point
✓ For a limit to exist, left and right limits must match
✓ 0/0 is indeterminate - you need to simplify first
✓ For limits at infinity, divide by the highest power
✓ Continuity requires the limit to exist AND equal the function value
✓ Always try direct substitution first!

Remember: Limits are all about getting close, not necessarily arriving!`,
  questions: [
    {
      id: 1,
      question: 'Find lim[x→5] (3x² - 2x + 7)',
      solution: `**Step 1:** This is a polynomial, so use direct substitution
Substitute x = 5 directly into the function

**Step 2:** Calculate
= 3(5)² - 2(5) + 7
= 3(25) - 10 + 7
= 75 - 10 + 7
= 72

**Answer:** 72`
    },
    {
      id: 2,
      question: 'Find lim[x→3] (x² - 9)/(x - 3)',
      solution: `**Step 1:** Try direct substitution
= (3² - 9)/(3 - 3) = 0/0 (indeterminate form)

**Step 2:** Factor the numerator (difference of squares)
(x² - 9)/(x - 3) = (x - 3)(x + 3)/(x - 3)

**Step 3:** Cancel the common factor (x - 3)
= x + 3 (for x ≠ 3)

**Step 4:** Now substitute x = 3
= 3 + 3 = 6

**Answer:** 6`
    },
    {
      id: 3,
      question: 'Find lim[x→0] (√(x + 9) - 3)/x',
      solution: `**Step 1:** Direct substitution gives 0/0 (indeterminate)

**Step 2:** Multiply by the conjugate
= [(√(x + 9) - 3)/x] × [(√(x + 9) + 3)/(√(x + 9) + 3)]

**Step 3:** Multiply numerators (difference of squares)
= [(x + 9) - 9]/[x(√(x + 9) + 3)]
= x/[x(√(x + 9) + 3)]

**Step 4:** Cancel x
= 1/(√(x + 9) + 3)

**Step 5:** Substitute x = 0
= 1/(√9 + 3)
= 1/(3 + 3)
= 1/6

**Answer:** 1/6`
    },
    {
      id: 4,
      question: 'Find lim[x→∞] (2x³ + 5x)/(x³ - 3x² + 1)',
      solution: `**Step 1:** Divide every term by x³ (highest power in denominator)
= (2 + 5/x²)/(1 - 3/x + 1/x³)

**Step 2:** As x → ∞, terms with x in denominator approach 0
= (2 + 0)/(1 - 0 + 0)
= 2/1
= 2

**Answer:** 2`
    },
    {
      id: 5,
      question: 'Find lim[x→2] (x³ - 8)/(x - 2)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Factor the numerator (difference of cubes)
x³ - 8 = (x - 2)(x² + 2x + 4)

**Step 3:** Rewrite the limit
= (x - 2)(x² + 2x + 4)/(x - 2)

**Step 4:** Cancel (x - 2)
= x² + 2x + 4

**Step 5:** Substitute x = 2
= (2)² + 2(2) + 4
= 4 + 4 + 4
= 12

**Answer:** 12`
    },
    {
      id: 6,
      question: 'Evaluate lim[x→4] (x - 4)/(√x - 2)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Multiply by conjugate of denominator
= [(x - 4)/(√x - 2)] × [(√x + 2)/(√x + 2)]

**Step 3:** Simplify denominator (difference of squares)
= (x - 4)(√x + 2)/(x - 4)

**Step 4:** Cancel (x - 4)
= √x + 2

**Step 5:** Substitute x = 4
= √4 + 2
= 2 + 2
= 4

**Answer:** 4`
    },
    {
      id: 7,
      question: 'Find lim[x→-1] (x² + 3x + 2)/(x² - 1)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Factor numerator and denominator
Numerator: x² + 3x + 2 = (x + 1)(x + 2)
Denominator: x² - 1 = (x + 1)(x - 1)

**Step 3:** Rewrite
= (x + 1)(x + 2)/[(x + 1)(x - 1)]

**Step 4:** Cancel (x + 1)
= (x + 2)/(x - 1)

**Step 5:** Substitute x = -1
= (-1 + 2)/(-1 - 1)
= 1/(-2)
= -1/2

**Answer:** -1/2`
    },
    {
      id: 8,
      question: 'Find lim[x→∞] (5x² - 3x + 1)/(2x² + 7)',
      solution: `**Step 1:** Divide every term by x² (highest power)
= (5 - 3/x + 1/x²)/(2 + 7/x²)

**Step 2:** As x → ∞, terms with x in denominator → 0
= (5 - 0 + 0)/(2 + 0)
= 5/2

**Answer:** 5/2`
    },
    {
      id: 9,
      question: 'Evaluate lim[x→0] (1 - cos x)/x² (given that lim[x→0] (sin x)/x = 1)',
      solution: `**Step 1:** Use the identity 1 - cos x = 2sin²(x/2)
= lim[x→0] [2sin²(x/2)]/x²

**Step 2:** Rewrite x² as 4(x/2)²
= lim[x→0] [2sin²(x/2)]/[4(x/2)²]
= lim[x→0] (1/2) × [sin(x/2)/(x/2)]²

**Step 3:** Let u = x/2, as x → 0, u → 0
= (1/2) × [lim[u→0] (sin u)/u]²

**Step 4:** Apply the given limit
= (1/2) × (1)²
= 1/2

**Answer:** 1/2`
    },
    {
      id: 10,
      question: 'Find lim[x→∞] (3x + 5)/(x² - 2)',
      solution: `**Step 1:** Divide every term by x² (highest power in denominator)
= (3/x + 5/x²)/(1 - 2/x²)

**Step 2:** As x → ∞, all terms with x in denominator → 0
= (0 + 0)/(1 - 0)
= 0/1
= 0

**Answer:** 0`
    },
    {
      id: 11,
      question: 'Determine if lim[x→2] f(x) exists where f(x) = {x² if x < 2; 2x if x ≥ 2}',
      solution: `**Step 1:** Find the left-hand limit (x → 2⁻)
Use f(x) = x² for x < 2
lim[x→2⁻] x² = 2² = 4

**Step 2:** Find the right-hand limit (x → 2⁺)
Use f(x) = 2x for x ≥ 2
lim[x→2⁺] 2x = 2(2) = 4

**Step 3:** Compare the one-sided limits
Left limit = 4
Right limit = 4
Since they're equal, the limit exists!

**Answer:** Yes, lim[x→2] f(x) = 4`
    },
    {
      id: 12,
      question: 'Find lim[x→0] (√(1 + x) - 1)/x',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Multiply by conjugate
= [(√(1 + x) - 1)/x] × [(√(1 + x) + 1)/(√(1 + x) + 1)]

**Step 3:** Simplify numerator
= [(1 + x) - 1]/[x(√(1 + x) + 1)]
= x/[x(√(1 + x) + 1)]

**Step 4:** Cancel x
= 1/(√(1 + x) + 1)

**Step 5:** Substitute x = 0
= 1/(√1 + 1)
= 1/(1 + 1)
= 1/2

**Answer:** 1/2`
    },
    {
      id: 13,
      question: 'Find lim[x→-2] (x³ + 8)/(x + 2)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Factor numerator (sum of cubes)
x³ + 8 = (x + 2)(x² - 2x + 4)

**Step 3:** Rewrite
= (x + 2)(x² - 2x + 4)/(x + 2)

**Step 4:** Cancel (x + 2)
= x² - 2x + 4

**Step 5:** Substitute x = -2
= (-2)² - 2(-2) + 4
= 4 + 4 + 4
= 12

**Answer:** 12`
    },
    {
      id: 14,
      question: 'Evaluate lim[x→∞] (√(x² + 4x) - x)',
      solution: `**Step 1:** This gives ∞ - ∞ (indeterminate)
Multiply by conjugate

**Step 2:** Multiply by (√(x² + 4x) + x)/(√(x² + 4x) + x)
= [(x² + 4x) - x²]/(√(x² + 4x) + x)
= 4x/(√(x² + 4x) + x)

**Step 3:** Divide numerator and denominator by x
= 4/(√(1 + 4/x) + 1)

**Step 4:** As x → ∞, 4/x → 0
= 4/(√1 + 1)
= 4/2
= 2

**Answer:** 2`
    },
    {
      id: 15,
      question: 'Find lim[x→1] (x⁴ - 1)/(x - 1)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Factor numerator (difference of squares twice)
x⁴ - 1 = (x²)² - 1² = (x² - 1)(x² + 1)
= (x - 1)(x + 1)(x² + 1)

**Step 3:** Rewrite
= (x - 1)(x + 1)(x² + 1)/(x - 1)

**Step 4:** Cancel (x - 1)
= (x + 1)(x² + 1)

**Step 5:** Substitute x = 1
= (1 + 1)(1² + 1)
= (2)(2)
= 4

**Answer:** 4`
    },
    {
      id: 16,
      question: 'Find lim[x→0⁺] (1/x)',
      solution: `**Step 1:** Consider what happens as x approaches 0 from the right
x is positive and getting smaller

**Step 2:** As x gets closer to 0 (but stays positive):
- When x = 0.1, 1/x = 10
- When x = 0.01, 1/x = 100
- When x = 0.001, 1/x = 1000

**Step 3:** The values are increasing without bound
1/x → +∞

**Answer:** +∞ (the limit does not exist as a finite number)`
    },
    {
      id: 17,
      question: 'Evaluate lim[x→3] (2x² - 5x - 3)/(x - 3)',
      solution: `**Step 1:** Direct substitution gives 0/0

**Step 2:** Factor the numerator
Find factors of 2x² - 5x - 3
= (2x + 1)(x - 3)

**Step 3:** Rewrite
= (2x + 1)(x - 3)/(x - 3)

**Step 4:** Cancel (x - 3)
= 2x + 1

**Step 5:** Substitute x = 3
= 2(3) + 1
= 6 + 1
= 7

**Answer:** 7`
    },
    {
      id: 18,
      question: 'Find lim[x→∞] (4x³ - 2x² + 1)/(2x³ + 5x - 3)',
      solution: `**Step 1:** Divide every term by x³ (highest power)
= (4 - 2/x + 1/x³)/(2 + 5/x² - 3/x³)

**Step 2:** As x → ∞, terms with x in denominator → 0
= (4 - 0 + 0)/(2 + 0 - 0)
= 4/2
= 2

**Answer:** 2`
    },
    {
      id: 19,
      question: 'Find lim[x→0] (sin 3x)/(2x) (given lim[x→0] (sin x)/x = 1)',
      solution: `**Step 1:** Rewrite to use the standard limit
= lim[x→0] (3/2) × (sin 3x)/(3x)

**Step 2:** Let u = 3x, as x → 0, u → 0
= (3/2) × lim[u→0] (sin u)/u

**Step 3:** Apply the given limit
= (3/2) × 1
= 3/2

**Answer:** 3/2`
    },
    {
      id: 20,
      question: 'Determine if f(x) = (x² - 4)/(x - 2) is continuous at x = 2',
      solution: `**Step 1:** Check if f(2) is defined
f(2) = (2² - 4)/(2 - 2) = 0/0 (undefined)

**Step 2:** Since f(2) is not defined, the function is NOT continuous at x = 2

**Alternative check:** Find the limit
lim[x→2] (x² - 4)/(x - 2) = lim[x→2] (x - 2)(x + 2)/(x - 2)
= lim[x→2] (x + 2) = 4

**Step 3:** The limit exists (equals 4), but f(2) is undefined
This is a removable discontinuity (hole)

**Answer:** No, f(x) is not continuous at x = 2 (removable discontinuity)`
    }
  ]
};